<template>
  <div>
    <div v-if="swapOffer" class="mb-56 mt-32">
      <SwapSummaryCartV2
        v-if="
          experiments['experiment.swapSummaryCartV2'] ===
          'withSwapSummaryCartV2'
        "
        data-test="swap-summary"
        :price="swapOffer.standardPrice"
        :shipping-mode="swapOffer.defaultShippingModeId"
        :title="swapOffer.title"
        :total-price-after-buyback="totalPriceWithSwap"
        @delete-swap="handleDeleteSwapFromSummaryCart"
      />
      <SwapSummaryCart
        v-else
        data-qa="swap-summary"
        data-test="swap-summary"
        :details="swapOffer.diagnosticSummary"
        :price="swapOffer.standardPrice"
        :shipping-mode="swapOffer.defaultShippingModeId"
        :title="swapOffer.title"
        :total-price-after-buyback="totalPriceWithSwap"
        @delete-swap="handleDeleteSwapFromSummaryCart"
      />
      <SwapRemoveConfirmationCart
        :price="swapOffer.price"
        @remove="handleDeleteSwap"
      />
    </div>

    <template v-else-if="swapStore.advertisement">
      <RevCard class="mb-56 mt-32">
        <TheCatcherComponent>
          <SwapBlockCartV2
            v-if="
              experiments['experiment.swapBlockCartV2'] ===
              'withSwapBlockCartV2'
            "
            @see-more="handleSwapBlockCartV2Click('plp')"
            @see-swap="handleSwapBlockCartV2Click"
          />
          <SwapBlockCart
            v-else
            compact
            :has-offer="!!swapOffer"
            :modal-name="BUYBACK_MODAL_NAMES.SWAP_CART"
            :model="swapAdvertisementModel"
            :price="swapAdvertisementPrice"
            :swapModel="swapAdvertisementModel"
            @confirmation="handleSwapConfirmation"
          />
        </TheCatcherComponent>
      </RevCard>
    </template>
    <template v-if="swapOffer || swapStore.advertisement">
      <SwapModalWrapper
        :intro
        :modal-name="BUYBACK_MODAL_NAMES.SWAP_CART"
        :price="swapModalAdvertisementPrice"
        :zone
        @confirmation="handleSwapConfirmation"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { BuybackOffer, Price } from '@backmarket/http-api'
import type { CartItem } from '@backmarket/http-api/src/api-specs-checkout/cart/cart.types'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useBuybackOffer'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevCard } from '@ds/components/Card'
import { openModal } from '@ds/components/ModalBase'

import TheCatcherComponent from '~/scopes/buyback/components/TheCatcher/TheCatcherComponent.vue'
import {
  MODAL_NAMES as BUYBACK_MODAL_NAMES,
  MODAL_NAMES,
} from '~/scopes/buyback/constants'
import SwapBlockCart from '~/scopes/buyback/swap/components/SwapBlockCart/SwapBlockCart.vue'
import SwapBlockCartV2 from '~/scopes/buyback/swap/components/SwapBlockCart/SwapBlockCartV2.vue'
import SwapModalWrapper from '~/scopes/buyback/swap/components/SwapModalWrapper/SwapModalWrapper.vue'
import SwapRemoveConfirmationCart from '~/scopes/buyback/swap/components/SwapRemoveConfirmationCart/SwapRemoveConfirmationCart.vue'
import SwapSummaryCart from '~/scopes/buyback/swap/components/SwapSummaryCart/SwapSummaryCart.vue'
import SwapSummaryCartV2 from '~/scopes/buyback/swap/components/SwapSummaryCart/SwapSummaryCartV2.vue'
import { useSwapStore } from '~/scopes/checkout/stores/swapStore'

import useHandleUnauthorizedUser from '../../../../composables/useHandleUnauthorizedUser'

import translations from './Swap.translations'

const props = defineProps<{
  items: Array<CartItem>
  swapOffer: BuybackOffer | null
  totalPriceWithSwap: Price | undefined
  zone: string
}>()

const emit = defineEmits(['updated'])

const { handleUnauthorizedUser } = useHandleUnauthorizedUser()
const { resetBuybackOffer } = useBuybackOffer()
const swapStore = useSwapStore()
const tracking = useTracking()
const i18n = useI18n()
const experiments = useExperiments()
const $httpFetch = useHttpFetch()
const intro = ref<'plp'>()

const swapAdvertisementModel = computed(() => {
  return props.items.length === 1
    ? props.items[0].model
    : i18n(translations.swapMultipleItemsTitle)
})

const swapAdvertisementPrice = computed(() => {
  return i18n.price(swapStore.advertisement?.cartPriceWithSwapDiscount ?? '')
})

const swapModalAdvertisementPrice = computed(() => {
  if (!swapStore.advertisement?.cartPriceWithSwapDiscount) return undefined
  if (!swapStore.swapCurrency) return undefined

  return {
    amount: swapStore.advertisement.cartPriceWithSwapDiscount.toString(),
    currency: swapStore.swapCurrency,
  }
})

const handleSwapConfirmation = async () => {
  emit('updated')
}

const handleDeleteSwap = async () => {
  try {
    await $httpFetch('/bm/cart/swap', { method: 'DELETE' })
    const eventName = 'delete_swap'

    tracking.trackSwap({
      label: eventName,
      action: 'funnel > Step 4',
      ...(props.swapOffer && props.swapOffer.diagnosticPayload),
    })

    tracking.trackClick({
      name: eventName,
      value: {
        ...(props.swapOffer && props.swapOffer.diagnosticPayload),
        swap_estimation: props.swapOffer?.price,
      },
      zone: 'swap',
    })

    emit('updated')
    resetBuybackOffer()
  } catch (error) {
    await handleUnauthorizedUser(
      error as Record<string, unknown>,
      '[CHECKOUT] Unhandled error deleting swap',
    )
  }
}

const handleDeleteSwapFromSummaryCart = () => {
  openModal(MODAL_NAMES.SWAP_REMOVE_CART)
}

const handleSwapBlockCartV2Click = (introName?: 'plp') => {
  intro.value = introName
  openModal(BUYBACK_MODAL_NAMES.SWAP_CART)
}
</script>
